// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react';
import Helmet from 'react-helmet';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  Icon,
  IconName,
  useSnackBar,
} from '@usealloy/component-library';

import { DEFAULT_TITLE } from '~/dashboard/constants';
import { openSupport } from '~/helpers/supportWidget';
import { getWindowDimensions } from '~/helpers/hooks';
import { AlloyLogoBlack } from './AlloyLogo';
import { Hero1 } from './graphics/Hero1';
import { Hero2 } from './graphics/Hero2';
import { Hero3 } from './graphics/Hero3';
import { Hero4 } from './graphics/Hero4';
import {
  StyledAuthContainerView,
  StyledAlloyLogoBox,
  StyledSupportButton,
  StyledAuthFooter,
  StyledBackButton,
  SupportWidgetPositionOverride,
} from './styles';
import { routes } from '~/helpers/routes';

const randomizedHeroGraphicsOrderNumber = Math.floor(Math.random() * (4 - 1 + 1)) + 1;

const AuthContainerView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const addSnackBar = useSnackBar();
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions(),
  );

  useEffect(() => {
    function onWindowResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  useEffect(() => {
    if (location?.state?.message) {
      const { text, type } = location.state.message;
      addSnackBar({ title: text, type });
    }
  }, [location, addSnackBar]);

  const heroGraphics = useMemo(() => {
    const { height } = windowDimensions;
    const width = windowDimensions.height * 0.68;
    switch (randomizedHeroGraphicsOrderNumber) {
      case 1:
        return <Hero1 width={width} height={height} />;
      case 2:
        return <Hero2 width={width} height={height} />;
      case 3:
        return <Hero3 width={width} height={height} />;
      case 4:
        return <Hero4 width={width} height={height} />;
    }

    return null;
  }, [windowDimensions]);

  return (
    <>
      <Helmet>
        <title>Sign in | {DEFAULT_TITLE}</title>
      </Helmet>

      <SupportWidgetPositionOverride />

      <StyledAuthContainerView>
        <Grid colGap={2} rowGap={6}>
          <Grid.Item
            columns={{ sm: 4, md: 4, lg: 4 }}
            style={{
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <StyledAlloyLogoBox
              onClick={() => navigate(routes.v3.dashboard.authentication.login)}
            >
              <AlloyLogoBlack />
            </StyledAlloyLogoBox>

            {heroGraphics}
          </Grid.Item>
          <Grid.Item
            columns={{ sm: 1, md: 1, lg: 1 }}
            style={{ padding: '32px 32px 0px 0px' }}
          >
            {!location?.pathname.includes('dashboard/login') && (
              <StyledBackButton
                onClick={() => navigate(routes.v3.dashboard.authentication.login)}
              >
                <Icon
                  title="Back"
                  color="var(--cl-text-default)"
                  icon={IconName.ArrowLeft}
                  size="large"
                />
                <span>Return to Sign In</span>
              </StyledBackButton>
            )}
          </Grid.Item>
          <Grid.Item
            columns={{ sm: 7, md: 6, lg: 6 }}
            style={{
              justifyContent: 'center',
              display: 'flex',
            }}
          >
            <StyledSupportButton
              type="button"
              onClick={() => openSupport({
                offsetOverride: { horizontal: '20px', vertical: '50px' },
                positionOverride: { horizontal: 'right', vertical: 'top' },
                // Fallback for the CSS override above: if that selector stops matching,
                // Intercom still docks right instead of its default left/bottom.
                intercomPositionOverride: { alignment: 'right', horizontal_padding: 20 },
                onRateLimited: () => addSnackBar({
                  title: 'Something went wrong loading support. Please reload the page and try again.',
                  type: 'danger',
                }),
              })}
            >
              <Icon
                title="Support"
                color="var(--cl-text-default)"
                icon={IconName.Help}
                size="large"
              />
              <span>Support</span>
            </StyledSupportButton>

            <Outlet />
            <StyledAuthFooter>
              Copyright 2015-{new Date().getFullYear()}
              &nbsp;| First Mile Group, Inc. DBA Alloy | All Rights Reserved
            </StyledAuthFooter>
          </Grid.Item>
        </Grid>
      </StyledAuthContainerView>
    </>
  );
};

export default AuthContainerView;
